import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { Eye, Edit, MailCheck, UserCheck } from "lucide-react";
import { Link } from "react-router-dom";
import {
  getCandidateStatusLabel,
  getEvaluationRoundLabel,
} from "@/utils/formatters";
import { getCandidateStatusColor } from "@/utils/statusColors";
import { CANDIDATE_STATUS, getBranchTypeLabel } from "@/types/enums";
import type { Tables } from "@/integrations/supabase/types";
import CandidateAssessmentActions from "./CandidateAssessmentActions";
import { SendOfferLetterDialog } from "./evaluation/SendOfferLetterDialog";

export type Candidate = Tables<"candidates"> & {
  colleges?: { name: string } | null;
  job_positions?: { role_name: string } | null;
  evaluation_rounds?: { round_name: string; score: number | null }[];
  evaluation_email_sent?: boolean | null;
  offer_email_sent_at?: string | null;
  final_round_date?: string | null;
  commitment_amount?: string | null;
  offer_validity_date?: string | null;
  document_submission_deadline?: string | null;
};

interface CandidateTableProps {
  candidates: Candidate[];
  isLoading: boolean;
  itemsPerPage: number;
  onView: (candidate: Candidate) => void;
  onEdit: (id: string) => void;
  selectedIds?: Set<string>;
  onSelectCandidate?: (candidateId: string, checked: boolean) => void;
  showCheckboxes?: boolean;
  canManageCandidates?: boolean;
  onUpdateCandidate: () => void;
}

export function CandidateTable({
  candidates,
  isLoading,
  itemsPerPage,
  onView,
  onEdit,
  selectedIds = new Set(),
  onSelectCandidate,
  showCheckboxes = false,
  canManageCandidates = true,
  onUpdateCandidate,
}: CandidateTableProps) {
  const formatContactInfo = (candidate: Candidate) => {
    return (
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <span className="font-medium">{candidate.full_name}</span>
          {candidate.offer_email_sent_at && (
            <Badge
              className="bg-green-100 text-green-800 hover:bg-green-100 text-xs whitespace-nowrap"
              title={`Offer sent on ${new Date(candidate.offer_email_sent_at).toLocaleString()}`}
            >
              ✓ Offer Letter Sent
            </Badge>
          )}
        </div>
        <div className="text-sm text-gray-600">{candidate.email}</div>
        {candidate.phone && (
          <div className="text-sm text-gray-600">{candidate.phone}</div>
        )}
      </div>
    );
  };

  const formatAcademicInfo = (candidate: Candidate) => {
    return (
      <div className="space-y-1">
        <div className="text-sm">{candidate.colleges?.name || "N/A"}</div>
        {candidate.cgpa && (
          <div className="text-sm text-gray-600">CGPA: {candidate.cgpa}</div>
        )}
        {candidate.branch && (
          <div className="text-sm text-gray-600">
            {getBranchTypeLabel(candidate.branch as any)}
          </div>
        )}
      </div>
    );
  };

  const formatStatusAndRound = (candidate: Candidate) => {
    return (
      <div className="space-y-2">
        <Badge className={getCandidateStatusColor(candidate.status)}>
          {getCandidateStatusLabel(candidate.status as any)}
        </Badge>
        <div>
          <Badge variant="outline">
            {candidate.current_round
              ? getEvaluationRoundLabel(candidate.current_round as any)
              : "Not Started"}
          </Badge>
        </div>
      </div>
    );
  };

  const formatAllScores = (candidate: Candidate) => {
    const scores = [];

    // Add final score first
    if (candidate.final_score) {
      scores.push(
        <Badge
          key="final"
          variant="default"
          className="whitespace-nowrap text-xs mr-1 mb-1"
        >
          Final: {candidate.final_score}%
        </Badge>
      );
    }

    // Add evaluation round scores
    if (candidate.evaluation_rounds && candidate.evaluation_rounds.length > 0) {
      candidate.evaluation_rounds.forEach((er, idx) => {
        scores.push(
          <Badge
            key={idx}
            variant="secondary"
            className="whitespace-nowrap text-xs mr-1 mb-1"
          >
            {getEvaluationRoundLabel(er.round_name as any)}:{" "}
            {er.score != null ? `${er.score}%` : "N/A"}
          </Badge>
        );
      });
    }

    return scores.length > 0 ? (
      <div className="flex flex-wrap gap-1">{scores}</div>
    ) : (
      <span className="text-gray-400">N/A</span>
    );
  };

  return (
    <div className="space-y-4 overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            {showCheckboxes && <TableHead className="w-12">Select</TableHead>}
            <TableHead>Contact Info</TableHead>
            <TableHead>Academic Info</TableHead>
            <TableHead>Position</TableHead>
            <TableHead>Email Sent</TableHead>
            <TableHead>Status & Round</TableHead>
            <TableHead>All Scores</TableHead>
            <TableHead className="whitespace-nowrap">Registered At</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            Array.from({ length: itemsPerPage }).map((_, index) => (
              <TableRow key={index}>
                <TableCell colSpan={showCheckboxes ? 9 : 8}>
                  <Skeleton className="h-8 w-full" />
                </TableCell>
              </TableRow>
            ))
          ) : candidates.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={showCheckboxes ? 9 : 8}
                className="h-24 text-center"
              >
                No candidates found.
              </TableCell>
            </TableRow>
          ) : (
            candidates.map((candidate) => (
              <TableRow key={candidate.id}>
                {showCheckboxes && (
                  <TableCell>
                    <Checkbox
                      checked={selectedIds.has(candidate.id)}
                      onCheckedChange={(checked) =>
                        onSelectCandidate?.(candidate.id, checked as boolean)
                      }
                    />
                  </TableCell>
                )}
                <TableCell className="min-w-[200px]">
                  {formatContactInfo(candidate)}
                </TableCell>
                <TableCell className="min-w-[180px]">
                  {formatAcademicInfo(candidate)}
                </TableCell>
                <TableCell>
                  {candidate.job_positions?.role_name || "N/A"}
                </TableCell>
                <TableCell>
                  {candidate.evaluation_email_sent ? "Yes" : "No"}
                </TableCell>
                <TableCell className="min-w-[120px]">
                  {formatStatusAndRound(candidate)}
                </TableCell>
                <TableCell className="min-w-[250px]">
                  {formatAllScores(candidate)}
                </TableCell>
                <TableCell className="min-w-[110px] text-sm text-gray-600 whitespace-nowrap">
                  {candidate.created_at ? (
                    <div>
                      <div>{new Date(candidate.created_at).toLocaleDateString('en-GB')}</div>
                      <div className="text-xs text-gray-400">
                        {new Date(candidate.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}
                      </div>
                    </div>
                  ) : (
                    'N/A'
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onView(candidate)}
                      title="View"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    {canManageCandidates && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onEdit(candidate.id)}
                        title="Edit"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      title="Evaluate"
                      asChild
                    >
                      <Link to={`/admin/candidates/${candidate.id}/evaluate`}>
                        <UserCheck className="h-4 w-4" />
                      </Link>
                    </Button>
                    {/* <Button
                      variant="ghost"
                      size="icon"
                      onClick={() =>
                        window.open(
                          `/admin/candidates/${candidate.id}/voice-interview`,
                          "_blank"
                        )
                      }
                      title="Voice Interview"
                    >
                      <Mic className="h-4 w-4" />
                    </Button> */}
                    {canManageCandidates && (
                      <CandidateAssessmentActions
                        candidate={candidate}
                        onUpdate={onUpdateCandidate}
                      />
                    )}
                    {canManageCandidates &&
                      (candidate.status === CANDIDATE_STATUS.SELECTED ||
                        candidate.status === CANDIDATE_STATUS.OFFERED) && (
                        <SendOfferLetterDialog
                          candidate={candidate}
                          onSent={onUpdateCandidate}
                          trigger={
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Send Offer Mail"
                              disabled={!candidate.email}
                            >
                              <MailCheck className="h-4 w-4" />
                            </Button>
                          }
                        />
                      )}
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
